import { IdentifierTypeEnum } from "@repo/graphql/gql/graphql";
import { checkCuit, cleanCuit, formatCuit } from "../cuit";
import { checkNit, cleanNit } from "../nit";
import { checkRfc } from "../rfc";
import { checkRuc, formatRuc } from "../ruc";
import { checkRut, prettifyRut, removeSeparators } from "../rut";

/**
 * How one document type is cleaned, shown and validated. Mirrors the
 * backend's `IdentifierHandler` (`back/backend/resources/identifiers`), which
 * is the source of truth: a value valid here must be valid there.
 */
export type IdentifierHandler = {
  /** Turns what was typed into the value stored. Runs on every keystroke. */
  clean: (input: string) => string;
  /** How the stored value is displayed. */
  format: (value: string) => string;
  /** The backend's rules for the type. Takes the value as typed or stored. */
  validate: (value: string) => boolean;
  invalidMessage: string;
  /**
   * Whether the input shows `format(value)` while typing. Alphanumeric
   * documents are shown as typed, only uppercased.
   */
  formatWhileTyping: boolean;
};

const SEPARATORS = /[\s.-]/g;

/** The backend's `normalize`: no spaces, dots or dashes, upper case. */
const normalize = (value: string): string =>
  value.replace(SEPARATORS, "").toUpperCase();

const digitsOnly = (value: string, maxLength: number): string =>
  value.replace(/\D/g, "").slice(0, maxLength);

/** `12345678` -> `12.345.678`, keeping leading zeros. */
const groupThousands = (digits: string): string => {
  const head = digits.length % 3 || 3;
  const groups = [digits.slice(0, head)];
  for (let index = head; index < digits.length; index += 3) {
    groups.push(digits.slice(index, index + 3));
  }
  return groups.join(".");
};

/** The backend's `compact_rut`: `12345678-9`, or the stripped input if it is no RUT. */
const compactRut = (value: string): string => {
  const stripped = normalize(value);
  if (stripped.length < 2) return stripped;
  const body = stripped.slice(0, -1);
  const verifier = stripped.slice(-1);
  if (!/^\d+$/.test(body) || !/^[\dK]$/.test(verifier)) return stripped;
  return `${body.replace(/^0+(?=\d)/, "")}-${verifier}`;
};

const RUT_PATTERN = /^\d{1,8}-[\dK]$/;

const cleanRut = (input: string): string => {
  // The last character is the verifier: a K anywhere else cannot be part of a RUT.
  const stripped = removeSeparators(input)
    .toUpperCase()
    .replace(/^0+/, "")
    .replace(/K(?=.)/g, "")
    .slice(0, 9);
  return stripped.length < 2 ? stripped : compactRut(stripped);
};

const formatRutValue = (value: string): string => {
  const compact = compactRut(value);
  return RUT_PATTERN.test(compact) ? prettifyRut(compact) : compact;
};

const validateRut = (value: string): boolean => {
  const compact = compactRut(value);
  return RUT_PATTERN.test(compact) && checkRut(compact);
};

const validateRuc = (value: string): boolean => {
  const normalized = normalize(value);
  return /^\d{11}$/.test(normalized) && checkRuc(normalized);
};

const validateCuit = (value: string): boolean => {
  const normalized = normalize(value);
  return /^\d{11}$/.test(normalized) && checkCuit(normalized);
};

/** `9-10` base digits plus the check digit, `900.123.456-7`. */
const formatNitValue = (value: string): string => {
  const digits = cleanNit(value);
  if (digits.length <= 1) return digits;
  return `${groupThousands(digits.slice(0, -1))}-${digits.slice(-1)}`;
};

const validateNit = (value: string): boolean => {
  const normalized = normalize(value);
  return /^\d{10,11}$/.test(normalized) && checkNit(normalized);
};

const RFC_CHARACTERS = /^[A-ZÑ&0-9]*$/;

const validateRfc = (value: string): boolean => {
  const normalized = normalize(value);
  return RFC_CHARACTERS.test(normalized) && checkRfc(normalized);
};

type PatternRule = {
  pattern: RegExp;
  invalidMessage: string;
};

const asIs = (value: string): string => value;

/** A type whose only rule is its shape, shown as stored. */
const patternHandler = (
  { pattern, invalidMessage }: PatternRule,
  clean: (input: string) => string,
  format: (value: string) => string = asIs,
  formatWhileTyping = true,
): IdentifierHandler => ({
  clean,
  format: (value) => format(clean(value)),
  validate: (value) => pattern.test(normalize(value)),
  invalidMessage,
  formatWhileTyping,
});

const alphanumeric = (maxLength: number) => (input: string) =>
  normalize(input).slice(0, maxLength);

export const IDENTIFIER_HANDLERS: Record<
  IdentifierTypeEnum,
  IdentifierHandler
> = {
  [IdentifierTypeEnum.ClRut]: {
    clean: cleanRut,
    format: formatRutValue,
    validate: validateRut,
    invalidMessage: "RUT inválido",
    formatWhileTyping: true,
  },
  [IdentifierTypeEnum.PeRuc]: {
    clean: formatRuc,
    format: formatRuc,
    validate: validateRuc,
    invalidMessage: "RUC inválido",
    formatWhileTyping: true,
  },
  [IdentifierTypeEnum.PeDni]: patternHandler(
    { pattern: /^\d{8}$/, invalidMessage: "DNI inválido" },
    (input) => digitsOnly(input, 8),
  ),
  [IdentifierTypeEnum.PeForeignerCard]: patternHandler(
    {
      pattern: /^[A-Z0-9]{9,12}$/,
      invalidMessage: "Carnet de extranjería inválido",
    },
    alphanumeric(12),
    asIs,
    false,
  ),
  [IdentifierTypeEnum.PePassport]: patternHandler(
    { pattern: /^[A-Z0-9]{6,12}$/, invalidMessage: "Pasaporte inválido" },
    alphanumeric(12),
    asIs,
    false,
  ),
  [IdentifierTypeEnum.ArCuit]: {
    clean: cleanCuit,
    format: formatCuit,
    validate: validateCuit,
    invalidMessage: "CUIT inválido",
    formatWhileTyping: true,
  },
  [IdentifierTypeEnum.ArDni]: patternHandler(
    { pattern: /^\d{7,8}$/, invalidMessage: "DNI inválido" },
    (input) => digitsOnly(input, 8),
    groupThousands,
  ),
  [IdentifierTypeEnum.CoNit]: {
    clean: cleanNit,
    format: formatNitValue,
    validate: validateNit,
    invalidMessage: "NIT inválido",
    formatWhileTyping: true,
  },
  [IdentifierTypeEnum.CoCc]: patternHandler(
    { pattern: /^\d{6,10}$/, invalidMessage: "Cédula inválida" },
    (input) => digitsOnly(input, 10),
    groupThousands,
  ),
  [IdentifierTypeEnum.CoForeignerId]: patternHandler(
    { pattern: /^\d{3,10}$/, invalidMessage: "Cédula de extranjería inválida" },
    (input) => digitsOnly(input, 10),
    groupThousands,
  ),
  [IdentifierTypeEnum.MxRfc]: {
    clean: alphanumeric(13),
    format: asIs,
    validate: validateRfc,
    invalidMessage: "RFC inválido",
    formatWhileTyping: false,
  },
};
